import { useCallback, useState } from 'react';
import { TABLE_DENSITY_STORAGE_KEY, TableDensity, isTableDensity } from '@/components/ui/DataTable/density';

/**
 * Per-page keys from before SPR-1140. Read once so a user who already picked
 * "compact" on one board keeps that choice after the preference became global.
 */
const LEGACY_DENSITY_KEYS = [
  'transactionsTableDensity',
  'supportedsTableDensity',
  'supportRequestsTableDensity',
  'table.density.transactionsInquiry',
  'table.density.usersManagement',
  'table.density.loginAudit',
  'table.density.nedarimCredentials',
  'table.density.systemMessages',
  'table.density.supportedsManagement',
  'table.density.supportRequestsManagement',
  'table.density.fundGroups',
  'table.density.featureFlags',
  'table.density.offlineDonationsManagement',
  'table.density.holidayPeriods',
  'table.density.nedarimSync',
  'table.density.fundsManagement',
  'table.density.openFundRequests',
  'table.density.inquiriesManagement',
  'table.density.suppliersManagement',
  'table.density.classificationsManagement',
  'table.density.eligibilityOverrides',
  'table.density.childrenManagement',
  'table.density.standingOrders',
  'table.density.pendingDonations',
];

const readStoredDensity = (legacyKey: string | undefined, fallback: TableDensity): TableDensity => {
  try {
    const global = localStorage.getItem(TABLE_DENSITY_STORAGE_KEY);
    if (isTableDensity(global)) return global;

    const candidates = [legacyKey, ...LEGACY_DENSITY_KEYS].filter((key, index, all): key is string => Boolean(key) && all.indexOf(key) === index);
    for (const key of candidates) {
      const stored = localStorage.getItem(key);
      if (isTableDensity(stored)) {
        localStorage.setItem(TABLE_DENSITY_STORAGE_KEY, stored);
        return stored;
      }
    }
    return fallback;
  } catch {
    return fallback;
  }
};

/**
 * One site-wide row density, remembered across sessions and boards.
 * The optional `legacyStorageKey` is only consulted when the global key is empty.
 */
const useTableDensity = (legacyStorageKey?: string, fallback: TableDensity = 'normal'): [TableDensity, (density: TableDensity) => void] => {
  const [density, setDensityState] = useState<TableDensity>(() => readStoredDensity(legacyStorageKey, fallback));

  const setDensity = useCallback((next: TableDensity) => {
    setDensityState(next);
    try {
      localStorage.setItem(TABLE_DENSITY_STORAGE_KEY, next);
    } catch {
      // Private-mode / quota failures must not break the board.
    }
  }, []);

  return [density, setDensity];
};

export default useTableDensity;
